import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, MessageCircle } from "lucide-react";
import { LOGO_URL, api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/galerie", label: "Galerie" },
  { to: "/artistes", label: "Les Artistes" },
  { to: "/contact", label: "Contact" },
  { to: "/artistes-celebres", label: "Artistes Handicapés connus" },
  { to: "/encheres", label: "Enchères en Ligne" },
  { to: "/informations", label: "Informations" },
];

export const Navbar = () => {
  const { count } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!user || user.role === "admin") {
      setUnread(0);
      return;
    }
    const fetchUnread = () =>
      api.get("/messages/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 20000);
    return () => clearInterval(id);
  }, [user, location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="Logo œil bleu" className="h-12 w-12 object-contain" />
          <div className="leading-none hidden sm:block">
            <p className="font-display text-lg tracking-tight text-white">La Fondation</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Artistes Handicapés · France</p>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm tracking-wide transition-colors hover:text-white ${
                location.pathname === l.to ? "text-white" : "text-zinc-400"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4 md:ml-16 lg:ml-24">
          {user && user.role !== "admin" && (
            <Link
              to="/messagerie"
              data-testid="messages-button"
              className="relative p-2 text-zinc-200 hover:text-white transition-colors"
              aria-label="Messagerie"
            >
              <MessageCircle className="h-5 w-5" />
              {unread > 0 && (
                <span
                  data-testid="unread-badge"
                  className="absolute -top-1 -right-1 bg-[#E11D48] text-white text-[10px] h-5 w-5 flex items-center justify-center rounded-full"
                >
                  {unread}
                </span>
              )}
            </Link>
          )}
          <Link
            to={user ? (user.role === "admin" ? "/admin" : "/compte") : "/connexion"}
            data-testid="account-button"
            className="text-sm text-zinc-200 hover:text-white transition-colors whitespace-nowrap"
          >
            {user ? "Mon compte" : "Connexion / Inscription"}
          </Link>
          <Link
            to="/panier"
            data-testid="cart-button"
            className="relative p-2 text-zinc-200 hover:text-white transition-colors"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#E11D48] text-white text-[10px] h-5 w-5 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-white"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="text-zinc-200 text-lg" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
